import { comparisonRows } from '@/mocks/coursesData';
import { useTranslation } from 'react-i18next';

export default function ComparisonTable() {
  const { t } = useTranslation();
  return (
    <section className="relative py-20 md:py-28 bg-background-100/50">
      <div className="max-w-[1600px] mx-auto px-8 md:px-12 lg:px-16">
        <div className="text-center mb-12">
          {String(t('courses.comparison.label')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-accent-600 text-[13px] tracking-[0.2em] mb-5 font-label">{__s}</p>))}
          <h2 className="font-heading text-xl md:text-2xl text-foreground-900 tracking-[0.06em] mb-2">
            {t('courses.comparison.title')}
          </h2>
          {String(t('courses.comparison.subtitleEn')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="section-title-en text-accent-500 text-[15px] tracking-[0.12em] mb-4">{__s}</p>))}
          <div className="gold-line mx-auto"></div>
          {String(t('courses.comparison.lead')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[15px] text-foreground-500 tracking-[0.04em] leading-relaxed max-w-[650px] mx-auto mt-5">{__s}</p>))}
          <div className="max-w-[650px] mx-auto mt-4 px-5 py-3 bg-accent-500/10 border border-accent-500/30">
            {String(t('courses.comparison.inclusionNote')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[14px] text-accent-700 font-medium tracking-[0.02em] leading-relaxed">{__s}</p>))}
          </div>
        </div>

        {/* Desktop table */}
        <div className="hidden md:block overflow-hidden border border-background-200/60">
          <div className="grid grid-cols-[180px_1fr_1fr_1fr] bg-primary-900">
            <div className="p-4"></div>
            <div className="p-4 text-center">
              {String(t('courses.comparison.colEssential')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[13px] text-accent-400 tracking-[0.08em] font-medium">{__s}</p>))}
            </div>
            <div className="p-4 text-center">
              {String(t('courses.comparison.colAdvanced')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[13px] text-accent-400 tracking-[0.08em] font-medium">{__s}</p>))}
            </div>
            <div className="p-4 text-center">
              {String(t('courses.comparison.colMaster')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[13px] text-accent-400 tracking-[0.08em] font-medium">{__s}</p>))}
            </div>
          </div>

          <div className="divide-y divide-background-200/60">
            {comparisonRows.map((row, idx) => {
              const rowKeys = ['duration','hours','price','schedule','target','goal','content','cert','transfer'];
              const rowKey = rowKeys[idx] || idx.toString();
              const isPrice = rowKey === 'price';
              const isCert = rowKey === 'cert';
              return (
                <div
                  key={idx}
                  className={`grid grid-cols-[180px_1fr_1fr_1fr] ${
                    idx % 2 === 0 ? 'bg-background-50' : 'bg-background-100/40'
                  }`}
                >
                  <div className="p-4 flex items-center">
                    <span className="text-[14px] text-foreground-700 tracking-[0.05em] font-medium">
                      {t(`courses.row.${rowKey}`)}
                    </span>
                  </div>
                  <div className="p-4 text-center flex items-center justify-center">
                    <span className={`text-[14px] tracking-[0.04em] leading-relaxed ${
                      isPrice ? 'text-accent-600 font-medium text-[15px]' :
                      isCert ? 'text-accent-600' :
                      'text-foreground-600'
                    }`}>
                      {t(`courses.row.essential.${rowKey}`)}
                    </span>
                  </div>
                  <div className={`p-4 text-center flex items-center justify-center ${idx % 2 === 0 ? 'bg-background-50' : 'bg-background-100/40'}`}>
                    <span className={`text-[14px] tracking-[0.04em] leading-relaxed ${
                      isPrice ? 'text-accent-600 font-medium text-[15px]' :
                      isCert ? 'text-accent-600' :
                      'text-foreground-600'
                    }`}>
                      {t(`courses.row.advanced.${rowKey}`)}
                    </span>
                  </div>
                  <div className="p-4 text-center flex items-center justify-center">
                    <span className={`text-[14px] tracking-[0.04em] leading-relaxed ${
                      isPrice ? 'text-accent-600 font-medium text-[15px]' :
                      isCert ? 'text-accent-600' :
                      'text-foreground-600'
                    }`}>
                      {t(`courses.row.master.${rowKey}`)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden space-y-6">
          {[t('courses.comparison.colEssential'), t('courses.comparison.colAdvanced'), t('courses.comparison.colMaster')].map((course, cIdx) => {
            const key = cIdx === 0 ? 'essential' : cIdx === 1 ? 'advanced' : 'master';
            return (
              <div key={cIdx} className="border border-background-200/60">
                <div className="bg-primary-900 p-4 text-center">
                  <p className="text-[14px] text-accent-400 tracking-[0.08em] font-medium">{course}</p>
                </div>
                <div className="divide-y divide-background-200/60">
                  {comparisonRows.map((_row, rIdx) => {
                    const rowKeys2 = ['duration','hours','price','schedule','target','goal','content','cert','transfer'];
                    const rk = rowKeys2[rIdx] || rIdx.toString();
                    const isP = rk === 'price';
                    const isC = rk === 'cert';
                    return (
                    <div key={rIdx} className={`p-4 ${rIdx % 2 === 0 ? 'bg-background-50' : 'bg-background-100/40'}`}>
                      <p className="text-[12px] text-foreground-400 tracking-[0.06em] mb-1.5">{t(`courses.row.${rk}`)}</p>
                      <p className={`text-[14px] tracking-[0.04em] leading-relaxed ${
                        isP ? 'text-accent-600 font-medium text-[15px]' :
                        isC ? 'text-accent-600' :
                        'text-foreground-700'
                      }`}>
                        {t(`courses.row.${key}.${rk}`)}
                      </p>
                    </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {String(t('courses.comparison.note')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[13px] text-foreground-400 tracking-[0.04em] mt-6 text-center">{__s}</p>))}
      </div>
    </section>
  );
}